import { GeoPin } from 'app/design/icons/geo-pin';
import { NumFavs } from 'app/design/icons/num-favs';
import { NumViews } from 'app/design/icons/num-views';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import { useListingCard } from 'app/hooks/use-listing-card';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { BonificationBadge } from 'app/screens/billboard/all-listings/components/bonification-banner';
import type { ListingOfferCardProps } from 'app/types/listing-offer-card-props';
import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { CorpoImage } from './corp-image';
import { FavouriteButton } from './favourite-button';
import { LoanFeeTag } from './listing-card-loan-fee-tag';
import { ListingPostedByMembers } from './listing-card-posted-by-members';
import { ListingThumbnail } from './listing-thumbnail';
import { Checkbox, createTheme as createDSTheme } from 'app/shopify';
import { IconEyeOff } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { getDateTag, isOfferListingNew, ListingCardTimeTag } from './listing-card-metadata';
import { Listing, ListingSubtype, ListingType } from 'app/types/listing';
import { OFFER_CARD_HEIGHT } from './listing-offer-card-old.small.constants';
const theme = createDSTheme();

const ListingOfferCardSmallOld = memo(function ListingOfferCardSmallOldComponent(props: ListingOfferCardProps) {
  const {
    continueTutorial,
    hideCorpoBadge = false,
    isFirst,
    isNew,
    isSelecting,
    listing,
    selectedRowsState,
    tutorialViewRef: firstOfferTutorialViewRef,
  } = props;
  const { t } = useTranslation('all-listings');
  const { categoryPrimary, item, type, subtype, before, groups, distance, listingsTags } = listing;

  const {
    isAuthor,
    isInvisible,
    tutorialViewRef,
    isSelected,
    displayNumViews,
    displayNumFavourites,
    thumbPicture,
    onPress,
    loanFeeAvailable,
  } = useListingCard({
    continueTutorial,
    isFirst,
    isSelecting,
    listing,
    selectedRowsState,
    tutorialViewRef: firstOfferTutorialViewRef,
  });

  const { formatDistanceWithoutText } = useDistanceUnit();
  const titleSizeBeforeOverflow = 40;
  const listingCardTestID = `listing-offer-card-${item.name}`;
  const displayedIsNew = isAuthor ? false : isNew;
  const hasTimeTag = Boolean(getDateTag(listing) || (displayedIsNew ?? isOfferListingNew(listing.createdAt)));

  return (
    <Pressable
      accessibilityRole={isSelecting ? 'checkbox' : 'button'}
      accessibilityState={isSelecting ? { checked: Boolean(isSelected) } : undefined}
      onPress={onPress}
      ref={tutorialViewRef}
      testID={listingCardTestID}
    >
      <Row alignItems="center" gap="spacing.general.x4">
        {isSelecting ? (
          <View
            pointerEvents="none"
            style={{ height: OFFER_CARD_HEIGHT, width: 24, flexDirection: 'row', alignItems: 'center' }}
          >
            <Checkbox selected={Boolean(isSelected)} showText={false} />
          </View>
        ) : null}
        <SmallOfferThumbnail
          isInvisible={isInvisible}
          before={before}
          listing={listing}
          thumbPicture={thumbPicture}
          subtype={subtype}
          type={type}
          displayedIsNew={displayedIsNew}
          groups={groups}
          hideCorpoBadge={hideCorpoBadge}
          hasTimeTag={hasTimeTag}
        />

        <SmallOfferDetails
          listing={listing}
          isAuthor={isAuthor}
          item={item}
          titleSizeBeforeOverflow={titleSizeBeforeOverflow}

          listingsTags={listingsTags}
          distance={distance}
          formatDistanceWithoutText={formatDistanceWithoutText}
          displayNumViews={displayNumViews}
          displayNumFavourites={displayNumFavourites}
          categoryPrimary={categoryPrimary}
          t={t}
          loanFeeAvailable={loanFeeAvailable}
        />
      </Row>
    </Pressable>
  );
});

const styles = StyleSheet.create({
  hiddenListingOverlay: {
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export { ListingOfferCardSmallOld };

function SmallOfferDetails({
  listing,
  isAuthor,
  item,
  titleSizeBeforeOverflow,
  listingsTags,
  distance,
  formatDistanceWithoutText,
  displayNumViews,
  displayNumFavourites,
  categoryPrimary,
  t,
  loanFeeAvailable,
}: {
  listing: Listing;
  isAuthor: boolean;
  item: { id: string; name: string };
  titleSizeBeforeOverflow: number;
  listingsTags: Listing['listingsTags'];
  distance: number | undefined;
  formatDistanceWithoutText: (distanceInMeters?: number) => string;
  displayNumViews: number | undefined;
  displayNumFavourites: number | undefined;
  categoryPrimary: Listing['categoryPrimary'];
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
  loanFeeAvailable: boolean;
}) {
  return (
    <Column flex={1} gap="spacing.general.x2">
      {listing.canBonify && isAuthor ? (
        <Row>
          <BonificationBadge />
          <View style={{ flex: 1 }} />
        </Row>
      ) : null}
      <Row alignItems="center" gap="spacing.general.x2">
        <Text fontWeight="700" numberOfLines={2} variant="bodyLarge" style={{ flexShrink: 1 }}>
          {item.name.length <= titleSizeBeforeOverflow
            ? item.name
            : `${item.name.slice(0, titleSizeBeforeOverflow)}...`}
        </Text>
        <View style={{ flex: 1 }} />
        {!isAuthor ? <FavouriteButton listing={listing} /> : null}
      </Row>
      <SmallOfferTags
        isAuthor={isAuthor}

        listingsTags={listingsTags}
        distance={distance}
        formatDistanceWithoutText={formatDistanceWithoutText}
      />

      <SmallOfferOwnerStats
        isAuthor={isAuthor}
        displayNumViews={displayNumViews}
        displayNumFavourites={displayNumFavourites}
        categoryPrimary={categoryPrimary}
        t={t}
      />
      {isAuthor && loanFeeAvailable && listing.loanFee ? <LoanFeeTag loanFee={listing.loanFee} /> : null}

      {!isAuthor ? (
        <Row alignItems="center" gap="spacing.general.x2">
          <ListingPostedByMembers
            avatarSize={loanFeeAvailable ? 24 : 30}
            grouping={listing.grouping}
            isAuthor={false}
            loanFee={listing.loanFee}
            user={listing.user}
          />
        </Row>
      ) : null}

      {loanFeeAvailable && !isAuthor && listing.loanFee ? <LoanFeeTag loanFee={listing.loanFee} /> : null}
    </Column>
  );
}

function SmallOfferOwnerStats({
  isAuthor,
  displayNumViews,
  displayNumFavourites,
  categoryPrimary,
  t,
}: {
  isAuthor: boolean;
  displayNumViews: number | undefined;
  displayNumFavourites: number | undefined;
  categoryPrimary: Listing['categoryPrimary'];
  t: ReturnType<typeof useTranslation<'all-listings'>>['t'];
}): React.ReactNode {
  return isAuthor ? (
    Number.isInteger(displayNumViews) && Number.isInteger(displayNumFavourites) ? (
      <Row alignItems="center" gap="spacing.general.x2" paddingVertical="spacing.general.x0,5">
        <Row alignItems="center">
          <NumViews />
          <Text fontSize={16} paddingLeft="spacing.general.x0,5" variant="bodyBase">
            {displayNumViews}
          </Text>
        </Row>
        <Row alignItems="center">
          <NumFavs />
          <Text fontSize={16} paddingLeft="spacing.general.x0,5" variant="bodyBase">
            {displayNumFavourites}
          </Text>
        </Row>
      </Row>
    ) : (
      <Row alignItems="center" paddingVertical="spacing.general.x0,5">
        <Row
          alignItems="center"
          borderRadius="radius.xl"
          paddingHorizontal="spacing.general.x1"
          paddingVertical="spacing.general.x1"
          style={{ backgroundColor: categoryPrimary.colorLight }}
        >
          <Text
            fontSize={12}
            fontWeight="600"
            paddingHorizontal="spacing.general.x1"
            style={{ color: categoryPrimary.colorDark }}
          >
            {t('manageOffer', { ns: 'common' })}
          </Text>
        </Row>
      </Row>
    )
  ) : null;
}

function SmallOfferTags({
  isAuthor,
  listingsTags,
  distance,
  formatDistanceWithoutText,
}: {
  isAuthor: boolean;
  listingsTags: Listing['listingsTags'];
  distance: number | undefined;
  formatDistanceWithoutText: (distanceInMeters?: number) => string;
}): React.ReactNode {
  const hasTags = !!(listingsTags && listingsTags.length > 0);
  return !isAuthor ? (
    <Row
      alignItems="center"
      flexWrap="wrap"
      columnGap="spacing.general.x1"
      style={{ rowGap: theme.spacing['spacing.general.x2'] }}
    >
      {hasTags ? (
        <Row
          alignItems="center"
          backgroundColor="surface.tertiary-neutral"
          borderRadius="radius.xl"
          maxWidth="100%"
          paddingHorizontal="spacing.general.x1"
          paddingVertical="spacing.general.x1"
        >
          <Text color="text.secondary" fontSize={12} fontWeight="500" numberOfLines={1}>
            {listingsTags[0]?.tagName}
          </Text>
        </Row>
      ) : null}
      {hasTags && listingsTags.length > 1 ? (
        <Row
          alignItems="center"
          backgroundColor="surface.tertiary-neutral"
          borderRadius="radius.xl"
          paddingHorizontal="spacing.general.x1"
          paddingVertical="spacing.general.x1"
        >
          <Text color="text.secondary" fontSize={12}>
            +{listingsTags.length - 1}
          </Text>
        </Row>
      ) : null}
      {distance !== undefined ? (
        <Row alignItems="center" style={{ gap: 1 }}>
          <GeoPin color={theme.colors['icon.secondary']} height={12} width={12} />
          <Text fontSize={12} style={{ color: theme.colors['icon.secondary'] }}>
            {formatDistanceWithoutText(distance)}
          </Text>
        </Row>
      ) : null}
    </Row>
  ) : null;
}

function SmallOfferThumbnail({
  isInvisible,
  before,
  listing,
  thumbPicture,
  subtype,
  type,
  displayedIsNew,
  groups,
  hideCorpoBadge,
  hasTimeTag,
}: {
  isInvisible: boolean;
  before: string | null | undefined;
  listing: Listing;
  thumbPicture: string | undefined;
  subtype: ListingSubtype;
  type: ListingType;
  displayedIsNew: boolean | undefined;
  groups: Listing['groups'];
  hideCorpoBadge: boolean;
  hasTimeTag: boolean;
}) {
  return (
    <View style={{ position: 'relative' }}>
      <View style={{ opacity: isInvisible ? 0.3 : 1 }}>
        <ListingThumbnail
          before={before}
          isFound={!!listing.lender}
          picture={thumbPicture}
          subtype={subtype}
          type={type}
          size={OFFER_CARD_HEIGHT}
        />
      </View>
      {isInvisible ? (
        <View pointerEvents="none" style={[StyleSheet.absoluteFill, styles.hiddenListingOverlay]}>
          <IconEyeOff strokeWidth={ICON_STROKE_WIDTH} size={44} />
        </View>
      ) : null}
      {!isInvisible ? (
        <>
          <View style={{ position: 'absolute', top: 12, left: 12, maxWidth: '75%' }}>
            <ListingCardTimeTag isNew={displayedIsNew} listing={listing} />
          </View>
          {groups?.length && !hideCorpoBadge ? (
            <CorpoImage
              picture={groups[0]?.picture}
              style={{ position: 'absolute', top: hasTimeTag ? 45 : 10, left: 14 }}
            />
          ) : null}
        </>
      ) : null}
    </View>
  );
}
